import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getPrimaryMenuLanguage,
  getPrimaryMenuName,
  getProductDisplayTitle,
  getProductDisplayDescription,
  getCategoryDisplayTitle,
  getSubcategoryDisplayTitle,
} from "./steps/utils";
import { formatDualMoney } from "../../../../utils/currency";
import { DEFAULT_MENU_DISPLAY, DEFAULT_RESTAURANT } from "./WizardVisualSettings";

const STEP_HINTS = [
  "wizardLiveHintMenu",
  "wizardLiveHintProducts",
  "wizardLiveHintProducts",
  "wizardLiveHintProducts",
  "wizardLiveHintProducts",
  "wizardLiveHintCategories",
  "wizardLiveHintLink",
  "wizardLiveHintReview",
];

const LANG_FLAGS = {
  en: "🇬🇧",
  fr: "🇫🇷",
  ar: "🇹🇳",
  es: "🇪🇸",
  de: "🇩🇪",
  it: "🇮🇹",
  pt: "🇵🇹",
  zh: "🇨🇳",
};

function WizardLivePhone({
  draft,
  menuPhotoPreview,
  staticMenuPreview,
  wizardPath,
  productPhotos = {},
  activeStep = 0,
  compact = false,
  featured = false,
  showStepLabel = true,
  emptyHintKey,
  productDetail = null,
}) {
  const { t } = useTranslation();
  const isStatic = wizardPath === "static";
  const isCustom = draft?.visualSettingsMode === "custom";
  const restaurant = isCustom
    ? { ...DEFAULT_RESTAURANT, ...(draft?.restaurant || {}) }
    : { ...DEFAULT_RESTAURANT };
  const menuDisplay = isCustom
    ? { ...DEFAULT_MENU_DISPLAY, ...(draft?.menu || {}) }
    : { ...DEFAULT_MENU_DISPLAY };
  const showLogo = restaurant.clientMenuShowLogo !== false;
  const showSearch = menuDisplay.searchBarHidden !== true;
  const showImages = menuDisplay.productImagesDisabled !== true;
  const showPrices = menuDisplay.productPricesHidden !== true;
  const onePage = restaurant.clientShowAllMenusOnOnePage === true;
  const menuLang = getPrimaryMenuLanguage(draft?.menu);
  const menuName = getPrimaryMenuName(draft?.menu) || t("wizardPreviewUntitledMenu");
  const langFlag = LANG_FLAGS[menuLang] || "🌐";

  const products = Array.isArray(draft?.products) ? draft.products : [];
  const categories = Array.isArray(draft?.categories) ? draft.categories : [];
  const subcategories = Array.isArray(draft?.subcategories) ? draft.subcategories : [];
  const relations = draft?.relations || {};

  const categoriesOrdered = useMemo(() => {
    const order = Array.isArray(relations.menuCategories) ? relations.menuCategories : [];
    const byId = new Map(categories.map((c) => [c.tempId, c]));
    const ordered = order.map((id) => byId.get(id)).filter(Boolean);
    return ordered.length ? ordered : categories;
  }, [categories, relations.menuCategories]);

  const productIdsByCategory = relations.categoryProducts || {};
  const productIdsBySubcategory = relations.subcategoryProducts || {};

  const productsById = useMemo(
    () => Object.fromEntries(products.map((p) => [p.tempId, p])),
    [products]
  );

  const [activeCategoryId, setActiveCategoryId] = useState(null);

  useEffect(() => {
    if (!categoriesOrdered.length) {
      setActiveCategoryId(null);
      return;
    }
    setActiveCategoryId((prev) => {
      if (prev && categoriesOrdered.some((c) => c.tempId === prev)) return prev;
      return categoriesOrdered[0].tempId;
    });
  }, [categoriesOrdered]);

  const getSubcatsForCategory = (categoryTempId) => {
    const order = relations.categorySubcategories?.[categoryTempId] || [];
    const byId = new Map(subcategories.map((s) => [s.tempId, s]));
    return order.map((id) => byId.get(id)).filter(Boolean);
  };

  const assignedIds = useMemo(() => {
    const assigned = new Set();
    Object.values(productIdsByCategory).forEach((ids) => {
      (ids || []).forEach((id) => assigned.add(id));
    });
    Object.values(productIdsBySubcategory).forEach((ids) => {
      (ids || []).forEach((id) => assigned.add(id));
    });
    return assigned;
  }, [productIdsByCategory, productIdsBySubcategory]);

  const unassignedProducts = products.filter((p) => !assignedIds.has(p.tempId));
  const activeCategory = categoriesOrdered.find((c) => c.tempId === activeCategoryId);
  const activeCategoryName = activeCategory
    ? getCategoryDisplayTitle(activeCategory, menuLang)
    : menuName;

  const contentSections = useMemo(() => {
    if (activeCategory) {
      const sections = [];
      const directIds = productIdsByCategory[activeCategory.tempId] || [];
      if (directIds.length) {
        sections.push({
          key: `cat-${activeCategory.tempId}`,
          title: activeCategoryName,
          productIds: directIds,
          pending: false,
        });
      }
      getSubcatsForCategory(activeCategory.tempId).forEach((sub) => {
        const ids = productIdsBySubcategory[sub.tempId] || [];
        sections.push({
          key: `sub-${sub.tempId}`,
          title: getSubcategoryDisplayTitle(sub, menuLang),
          productIds: ids,
          pending: false,
          orientation:
            sub.displayOrientation === "horizontal" ? "horizontal" : "vertical",
        });
      });
      if (sections.length) return sections;
    }

    if (products.length > 0 && !categoriesOrdered.length) {
      return [
        {
          key: "all-products",
          title: menuName,
          productIds: products.map((p) => p.tempId),
          pending: activeStep < 3,
        },
      ];
    }

    if (unassignedProducts.length > 0 && activeStep <= 3) {
      return [
        {
          key: "pending",
          title: activeCategoryName,
          productIds: unassignedProducts.map((p) => p.tempId),
          pending: true,
        },
      ];
    }

    return [];
  }, [
    activeCategory,
    activeCategoryName,
    activeStep,
    categoriesOrdered.length,
    menuName,
    menuLang,
    productIdsByCategory,
    productIdsBySubcategory,
    products,
    relations,
    subcategories,
    unassignedProducts,
  ]);

  const renderProductCard = (productId, isPending = false) => {
    const product = productsById[productId];
    if (!product) return null;
    const title = getProductDisplayTitle(product, menuLang);
    const description = getProductDisplayDescription(product, menuLang);
    const price = product.price ?? 0;
    const photoPreview = productPhotos?.[productId] || null;

    return (
      <article
        key={productId}
        className={`clic-product-card${isPending ? " clic-product-card--pending" : ""}`}
      >
        {showImages ? (
          <div className="clic-product-card__image">
            {photoPreview ? (
              <img src={photoPreview} alt="" className="clic-product-card__photo" />
            ) : (
              <i className="fa fa-utensils" aria-hidden="true" />
            )}
          </div>
        ) : null}
        <h4 className="clic-product-card__title">{title}</h4>
        {description ? <p className="clic-product-card__desc">{description}</p> : null}
        {showPrices ? (
          <p className="clic-product-card__price">
            {formatDualMoney(price, product.secondaryPrice)}
          </p>
        ) : null}
      </article>
    );
  };

  const renderContent = () => {
    if (isStatic && staticMenuPreview) {
      return (
        <div className="clic-phone__static-preview">
          {draft?.menu?.staticMenuMediaType === "pdf" ? (
            <iframe title={menuName} src={staticMenuPreview} className="clic-phone__static-pdf" />
          ) : (
            <img src={staticMenuPreview} alt={menuName} className="clic-phone__static-image" />
          )}
        </div>
      );
    }

    if (contentSections.length > 0) {
      return contentSections.map((section) => {
        const isHorizontal = section.orientation === "horizontal";
        return (
          <section
            key={section.key}
            className={`clic-phone__section${
              isHorizontal ? " clic-phone__section--horizontal" : ""
            }`}
          >
            <div className="clic-phone__section-head">
              <h3 className="clic-phone__section-title">{section.title}</h3>
              {isHorizontal ? (
                <i className="fa fa-chevron-right" aria-hidden="true" />
              ) : null}
            </div>
            <div
              className={`clic-phone__products${
                isHorizontal ? " clic-phone__products--horizontal" : ""
              }`}
            >
              {section.productIds.map((id) => renderProductCard(id, section.pending))}
            </div>
          </section>
        );
      });
    }

    return (
      <div className="clic-phone__empty">
        <p>{t(emptyHintKey || STEP_HINTS[activeStep] || "wizardLiveHintMenu")}</p>
      </div>
    );
  };

  const headerStyle = menuPhotoPreview
    ? { backgroundImage: `url(${menuPhotoPreview})` }
    : undefined;

  const renderProductDetail = () => {
    const title = productDetail.title || t("wizardPreviewUntitledProduct");
    const description = productDetail.description || "";
    const photoUrl = productDetail.photoUrl || null;
    const detailChoices = Array.isArray(productDetail.choices) ? productDetail.choices : [];

    return (
      <>
        <div className="clic-phone-product__hero">
          {photoUrl ? (
            <img src={photoUrl} alt="" />
          ) : (
            <div className="clic-phone-product__hero-empty" aria-hidden="true">
              <i className="fa fa-utensils" />
            </div>
          )}
        </div>
        <div className="clic-phone-product__body">
          <h2 className="clic-phone-product__title">{title}</h2>
          {description ? <p className="clic-phone-product__desc">{description}</p> : null}
          {showPrices && productDetail.hidePrice !== true ? (
            <p className="clic-phone-product__price">
              {formatDualMoney(productDetail.price ?? 0, productDetail.secondaryPrice)}
            </p>
          ) : null}
          {detailChoices.map((choice) => (
            <div key={choice.id} className="clic-phone-product__choice">
              <div className="clic-phone-product__choice-head">
                <span className="clic-phone-product__choice-title">
                  {choice.title || t("wizardPreviewUntitledChoice")}
                </span>
                {choice.obligatory === true ? (
                  <span className="clic-phone-product__required">
                    {t("wizardLiveChoiceRequired")}
                  </span>
                ) : null}
              </div>
              {choice.maxSelected > 0 ? (
                <p className="clic-phone-product__choice-hint">
                  {t("wizardLiveChooseBetween", {
                    min: choice.minSelected || 0,
                    max: choice.maxSelected,
                  })}
                </p>
              ) : null}
              {choice.items.map((item) => (
                <div key={item.id} className="clic-phone-product__item">
                  <span className="clic-phone-product__item-name">
                    {item.title || t("wizardPreviewUntitledItem")}
                  </span>
                  <span className="clic-phone-product__plus" aria-hidden="true">
                    +
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <button type="button" className="clic-phone-product__cart" tabIndex={-1}>
          {t("wizardLiveAddToCart")}
        </button>
      </>
    );
  };

  return (
    <div
      className={`wizard-live-phone${
        featured ? " wizard-live-phone--featured" : compact ? " wizard-live-phone--compact" : ""
      }`}
    >
      <div className="wizard-live-phone__meta">
        <span className="wizard-live-phone__live">
          <span className="wizard-live-phone__live-dot" aria-hidden="true" />
          {t("wizardPreviewLive")}
        </span>
        {showStepLabel ? (
          <span className="wizard-live-phone__step">
            {isStatic
              ? t("wizardLiveStepLabel", { step: activeStep + 1, total: 4 })
              : t("wizardLiveStepLabel", { step: activeStep + 1, total: 8 })}
          </span>
        ) : null}
      </div>

      {productDetail ? (
        <div className="clic-phone clic-phone--product">{renderProductDetail()}</div>
      ) : (
      <div
        className={`clic-phone${showSearch ? "" : " is-search-off"}${
          showLogo ? "" : " is-logo-off"
        }`}
      >
        <header className="clic-phone__hero">
          <div className="clic-phone__hero-bg" style={headerStyle} aria-hidden="true" />
          <div className="clic-phone__hero-top">
            <span className="clic-phone__lang" aria-hidden="true">
              {langFlag}
            </span>
            <span className="clic-phone__rating" aria-hidden="true">
              <span className="clic-phone__rating-g">G</span>
              <span className="clic-phone__rating-stars">★★★★★</span>
            </span>
          </div>

          {showLogo ? (
            <div className="clic-phone__logo-ring">
              {menuPhotoPreview ? (
                <img src={menuPhotoPreview} alt="" className="clic-phone__logo" />
              ) : (
                <div className="clic-phone__logo clic-phone__logo--placeholder">
                  <span>{menuName.slice(0, 2).toUpperCase()}</span>
                </div>
              )}
            </div>
          ) : null}

          <button type="button" className="clic-phone__menu-pill" tabIndex={-1}>
            {menuName}
            <i className="fa fa-chevron-down" aria-hidden="true" />
          </button>
        </header>

        {showSearch ? (
          <div className="clic-phone__search" aria-hidden="true">
            <i className="fa fa-search" />
            <span>{t("wizardLiveSearchPlaceholder")}</span>
          </div>
        ) : null}

        {categoriesOrdered.length > 0 && !onePage ? (
          <div className="clic-phone__chips" role="tablist">
            {categoriesOrdered.map((cat) => {
              const catId = cat.tempId;
              const isActive = catId === activeCategoryId;
              return (
                <button
                  key={catId}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`clic-phone__chip${isActive ? " is-active" : ""}`}
                  onClick={() => setActiveCategoryId(catId)}
                >
                  {getCategoryDisplayTitle(cat, menuLang)}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="clic-phone__scroll" key={`${activeCategoryId}-${activeStep}`}>
          {renderContent()}
        </div>

        <nav className="clic-phone__nav" aria-hidden="true">
          <div className="clic-phone__nav-item is-active">
            <span className="clic-phone__nav-icon">
              <i className="fa fa-utensils" />
            </span>
            <span>{t("wizardLiveNavMenu")}</span>
          </div>
          <div className="clic-phone__nav-item">
            <span className="clic-phone__nav-icon">
              <i className="fa fa-shopping-bag" />
              <em className="clic-phone__nav-badge">1</em>
            </span>
            <span>{t("wizardLiveNavCart")}</span>
          </div>
          <div className="clic-phone__nav-item clic-phone__nav-item--home">
            <span className="clic-phone__nav-home">
              <i className="fa fa-home" />
            </span>
            <span>{t("wizardLiveNavHome")}</span>
          </div>
          <div className="clic-phone__nav-item">
            <span className="clic-phone__nav-icon">
              <i className="fa fa-share-alt" />
            </span>
            <span>{t("wizardLiveNavSocial")}</span>
          </div>
          <div className="clic-phone__nav-item">
            <span className="clic-phone__nav-icon">
              <i className="fa fa-info-circle" />
            </span>
            <span>{t("wizardLiveNavInfo")}</span>
          </div>
        </nav>
      </div>
      )}
    </div>
  );
}

export default WizardLivePhone;
